import React, { useMemo } from "react";
import {
  Box,
  Typography,
  LinearProgress,
  Stack,
  Chip,
  Skeleton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import { PollResultsResponse } from "../../domain/polls/types";
import { enrichPollVotersWithAudience } from "../../utils/pollVoterDisplay";

type PollOption = { optionId: string; label: string; order: number };

type ViewerHint = { id?: string; name: string; email: string };

type ChoiceKind = "poll" | "rsvp";

type PollResultsSectionProps = {
  results: PollResultsResponse | null;
  loading: boolean;
  error?: string | null;
  fallbackOptions?: PollOption[];
  /** When API cannot map Cognito id → name, use viewers list for single-response notices */
  viewerHints?: ViewerHint[];
  choiceKind?: ChoiceKind;
};

const COPY: Record<
  ChoiceKind,
  {
    title: string;
    countLabel: (n: number) => string;
    who: string;
    secondary: (choice: string) => string;
    empty: string;
  }
> = {
  poll: {
    title: "Poll results",
    countLabel: (n) => `${n} vote${n === 1 ? "" : "s"}`,
    who: "Who voted",
    secondary: (choice) => `Voted for ${choice}`,
    empty: "No votes yet. Parents and students vote in the Noticeboard app.",
  },
  rsvp: {
    title: "RSVP summary",
    countLabel: (n) => `${n} response${n === 1 ? "" : "s"}`,
    who: "Who responded",
    secondary: (choice) => `RSVP: ${choice}`,
    empty: "No RSVPs yet.",
  },
};

const PollResultsSection: React.FC<PollResultsSectionProps> = ({
  results,
  loading,
  error,
  fallbackOptions = [],
  viewerHints = [],
  choiceKind = "poll",
}) => {
  const copy = COPY[choiceKind];
  const sortedFallback = [...fallbackOptions].sort((a, b) => a.order - b.order);
  const isClaimPoll = results?.pollMode === "claim";
  const optionsSource =
    isClaimPoll && results?.allOptions?.length
      ? results.allOptions
      : results?.options;
  const optionsToShow =
    optionsSource?.length
      ? [...optionsSource].sort((a, b) => a.order - b.order)
      : sortedFallback.map((opt) => ({
          ...opt,
          count: 0,
          percentage: 0,
        }));

  const optionLabelById = useMemo(() => {
    const map = new Map<string, string>();
    for (const opt of optionsToShow) {
      map.set(opt.optionId, opt.label);
    }
    return map;
  }, [optionsToShow]);

  const totalVotes = results?.totalVotes ?? 0;
  const isClosed = results?.isClosed === true;
  const voters = useMemo(
    () =>
      enrichPollVotersWithAudience(
        results?.responses ?? [],
        [],
        viewerHints
      ),
    [results?.responses, viewerHints]
  );

  if (error) {
    return (
      <Typography variant="body2" color="error" sx={{ mt: 2 }}>
        {error}
      </Typography>
    );
  }

  if (!results && !loading && sortedFallback.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        borderRadius: 2,
        bgcolor: "primary.50",
        border: "1px solid",
        borderColor: "primary.100",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} color="primary.main">
          {copy.title}
        </Typography>
        {loading && !results && (
          <Chip label="Updating…" size="small" variant="outlined" />
        )}
        {isClosed && (
          <Chip label="Closed" size="small" color="default" />
        )}
        {results && choiceKind === "poll" && (
          <Chip
            label={copy.countLabel(totalVotes)}
            size="small"
            variant="outlined"
          />
        )}
        {isClaimPoll && (
          <Chip label="Claim list" size="small" color="primary" variant="outlined" />
        )}
      </Stack>

      <Stack spacing={2}>
        {optionsToShow.map((opt) => (
          <Box key={opt.optionId}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 0.5 }}
            >
              <Typography variant="body2" fontWeight={500}>
                {opt.label}
              </Typography>
              {results ? (
                <Typography variant="body2" color="text.secondary">
                  {opt.count} ({opt.percentage}%)
                </Typography>
              ) : loading ? (
                <Skeleton variant="text" width={48} />
              ) : null}
            </Stack>
            {results ? (
              <LinearProgress
                variant="determinate"
                value={Math.min(100, opt.percentage)}
                sx={{ height: 8, borderRadius: 1 }}
              />
            ) : loading ? (
              <Skeleton
                variant="rounded"
                height={8}
                sx={{ borderRadius: 1 }}
              />
            ) : (
              <LinearProgress
                variant="determinate"
                value={0}
                sx={{ height: 8, borderRadius: 1, opacity: 0.35 }}
              />
            )}
          </Box>
        ))}
      </Stack>

      {results && voters.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography
            variant="subtitle2"
            fontWeight={600}
            color="text.primary"
            sx={{ mb: 1 }}
          >
            {copy.who}
          </Typography>
          <List dense disablePadding>
            {voters.map((vote) => {
              const choice =
                optionLabelById.get(vote.optionId) || "Unknown option";
              return (
                <ListItem
                  key={`${vote.userId}-${vote.respondedAt}`}
                  disableGutters
                  sx={{ py: 0.75 }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <PersonIcon
                      sx={{ fontSize: 20, color: "primary.main" }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={vote.voterName || "Unknown voter"}
                    secondary={`${copy.secondary(choice)}${
                      vote.voterEmail ? ` · ${vote.voterEmail}` : ""
                    }`}
                    primaryTypographyProps={{
                      variant: "body2",
                      fontWeight: 500,
                    }}
                    secondaryTypographyProps={{
                      variant: "caption",
                      color: "text.secondary",
                    }}
                  />
                </ListItem>
              );
            })}
          </List>
        </Box>
      )}

      {results && totalVotes === 0 && !loading && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 2, display: "block" }}
        >
          {copy.empty}
        </Typography>
      )}
    </Box>
  );
};

export default PollResultsSection;
